import type { ComponentType } from 'react';

/** Icon component (e.g. a lucide icon) rendered at the leading edge of an item. */
export type ContextMenuIcon = ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;

/** A selectable command in the context menu. */
export interface ContextMenuItem {
  type?: 'item';
  /** Stable id (used as React key + for tests). */
  id: string;
  label: string;
  /** Optional leading icon. */
  icon?: ContextMenuIcon;
  /** Muted, right-aligned shortcut hint (display only — not bound here). */
  shortcut?: string;
  /** Render as a destructive (danger) action. */
  danger?: boolean;
  /** Non-interactive when true. */
  disabled?: boolean;
  /** Invoked when the item is activated; the menu closes afterwards. */
  onSelect: () => void;
  /**
   * Optional "alternate" command surfaced while the Alt / Option key is held.
   * The menu's view swaps the label / icon / danger / onSelect of the entry
   * live for the duration of the modifier hold; clicking while Alt is held
   * invokes `altOnSelect` instead of `onSelect`. Used by the sidebar's trash
   * row to expose Delete Permanently as a safety-gated alternate without a
   * standalone open menu item or keyboard shortcut.
   */
  altLabel?: string;
  altIcon?: ContextMenuIcon;
  altDanger?: boolean;
  altOnSelect?: () => void;
}

/** A visual divider between groups of items. */
export interface ContextMenuSeparator {
  type: 'separator';
  /** Stable id (React key). */
  id: string;
}

export type ContextMenuEntry = ContextMenuItem | ContextMenuSeparator;

/** Imperative API exposed to consumers via {@link useContextMenu}. */
export interface ContextMenuApi {
  /** Open the menu at viewport coordinates (x, y) with the given entries. */
  openContextMenu: (x: number, y: number, items: ContextMenuEntry[]) => void;
  /** Close the menu if open (no-op otherwise). */
  closeContextMenu: () => void;
}

export function isSeparator(entry: ContextMenuEntry): entry is ContextMenuSeparator {
  return entry.type === 'separator';
}
